/**
 * A run of tool calls, as a card (RFC 0300 §4g).
 *
 * The CALLS fill of the one shell. `ToolCallGroup` drew the same run in its own
 * bordered block with its own header, and a thread with artifact cards beside
 * it read as two products. Now a run is a card like the rest: the verb says
 * what the run did, the label says what it was made of, and the rows under the
 * chevron are the calls, each still a `ToolCallBlock` with its arguments and
 * result one click further down.
 *
 * A LONE CALL IS STILL A CARD, not a bare row. The old group made an exception
 * for one call, on the grounds that wrapping one line in a disclosure hides it.
 * It does not here: the card's header IS the line, and the chevron reveals the
 * arguments and result, which is what the row's own chevron did.
 *
 * AN EDIT IS THE CASE THAT MATTERS. RFC 0298 §4f gives an edit its own row so
 * its diff is reachable, and `threadRows` still breaks the run for it. Here it
 * becomes a card whose verb is "Edited", whose label is the file, whose
 * trailing stat is the size of the change, and whose body is the diff, open by
 * itself when the change is small enough to read at a glance.
 */

import { useMemo } from 'react'
import { CardShell } from './CardShell'
import { EditDiffView, EditStat } from './EditDiffView'
import { describeEdit } from './editDiff'
import {
  AUTO_OPEN_LINES,
  ToolCallBlock,
  type ToolResult,
} from './ToolCallBlock'
import type { ToolCall } from './threadRows'
import {
  summarizeResult,
  summarizeToolArgs,
  summarizeToolNames,
} from './toolSummary'

export function ToolCallCard({
  calls,
  resultFor,
}: {
  calls: ToolCall[]
  resultFor: (toolUseId: string) => ToolResult | undefined
}) {
  const running = calls.find((c) => resultFor(c.id) === undefined)
  const errors = calls.filter((c) => resultFor(c.id)?.isError).length
  const only = calls.length === 1 ? calls[0] : undefined
  // Memoised because it runs an LCS, and the thread re-renders per token.
  const edit = useMemo(
    () => (only ? describeEdit(only.name, only.arguments) : null),
    [only],
  )

  if (only) {
    const result = resultFor(only.id)
    const pending = result === undefined
    const detail = edit?.label || summarizeToolArgs(only.arguments)
    const hint = summarizeResult(result)
    const trailing =
      edit && !result?.isError ? (
        <EditStat preview={edit} />
      ) : hint ? (
        <span
          className={`text-[0.65rem] ${result?.isError ? 'text-red-400' : 'text-muted-foreground/70'}`}
        >
          {hint}
        </span>
      ) : null
    return (
      <CardShell
        verb={
          edit ? (pending ? 'Editing' : 'Edited') : pending ? 'Running' : 'Ran'
        }
        label={edit ? detail : only.name}
        {...(!edit && detail ? { detail } : {})}
        pending={pending}
        {...(trailing ? { trailing } : {})}
        // The diff replaces the arguments; the two say the same thing. A small
        // edit opens by itself, which is RFC 0298's rule carried over.
        defaultOpen={
          edit !== null && edit.added + edit.removed <= AUTO_OPEN_LINES
        }
        body={
          edit ? (
            <div className="px-1 py-1">
              <EditDiffView preview={edit} />
              {result?.isError ? (
                <pre className="mt-1 overflow-x-auto rounded bg-black/40 p-2 font-mono text-[0.75rem] text-red-300 leading-relaxed">
                  {result.text || '(no output)'}
                </pre>
              ) : null}
            </div>
          ) : (
            <ToolCallBlock
              name={only.name}
              args={only.arguments}
              result={result}
              bare
            />
          )
        }
      />
    )
  }

  // While a call is in flight the header reports on it, since the run no
  // longer opens itself to show you. Once quiet, what the run was made of.
  const runningDetail = running
    ? [
        running.name,
        describeEdit(running.name, running.arguments)?.label ||
          summarizeToolArgs(running.arguments),
      ]
        .filter(Boolean)
        .join(' ')
    : undefined
  const trailing = running ? null : errors > 0 ? (
    // Failures are the one thing a collapsed run must still admit to.
    <span className="text-[0.65rem] text-red-400">{errors} failed</span>
  ) : null

  return (
    <CardShell
      verb={running ? 'Running' : 'Ran'}
      label={`${calls.length} tool calls`}
      detail={runningDetail ?? summarizeToolNames(calls.map((c) => c.name))}
      pending={Boolean(running)}
      {...(trailing ? { trailing } : {})}
      body={calls.map((call) => (
        <ToolCallBlock
          key={call.id}
          name={call.name}
          args={call.arguments}
          result={resultFor(call.id)}
        />
      ))}
    />
  )
}
